import React, { useRef, useEffect, useCallback } from "react";
import { Box, Typography, CircularProgress, Button } from "@mui/material";
import { MessageItem } from "./MessageItem";
import type { ProjectMessage, User } from "./types";

interface MessageListProps {
  messages: ProjectMessage[];
  loading: boolean;
  currentUserId: string | null;
  users: Map<string, User>;
  hasMore: boolean;
  onLoadMore: () => void;
  onReply: (message: ProjectMessage) => void;
  onEdit: (message: ProjectMessage) => void;
  onDelete: (messageId: string) => void;
  onToggleReaction: (messageId: string, emoji: string) => void;
}

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  loading,
  currentUserId,
  users,
  hasMore,
  onLoadMore,
  onReply,
  onEdit,
  onDelete,
  onToggleReaction,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevMessagesLengthRef = useRef(messages.length);
  const isInitialLoadRef = useRef(true);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    // On initial load or when new messages are added (not when loading older)
    if (messages.length > prevMessagesLengthRef.current || isInitialLoadRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: isInitialLoadRef.current ? "auto" : "smooth" });
      isInitialLoadRef.current = false;
    }
    prevMessagesLengthRef.current = messages.length;
  }, [messages.length]);

  // Handle scroll to top for infinite scroll
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container || loading || !hasMore) return;

    // Load more when scrolled near top
    if (container.scrollTop < 100) {
      onLoadMore();
    }
  }, [loading, hasMore, onLoadMore]);

  if (loading && messages.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (messages.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          color: "text.secondary",
          p: 4,
        }}
      >
        <Typography variant="h6" gutterBottom>
          No messages yet
        </Typography>
        <Typography variant="body2" textAlign="center">
          Be the first to send a message in this project chat!
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      onScroll={handleScroll}
      sx={{
        flex: 1,
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Load more button at top */}
      {hasMore && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
          <Button
            size="small"
            onClick={onLoadMore}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? "Loading..." : "Load older messages"}
          </Button>
        </Box>
      )}

      {/* Messages */}
      {messages.map((message) => (
        <MessageItem
          key={message._id}
          message={message}
          user={users.get(message.userId)}
          currentUserId={currentUserId}
          isOwnMessage={message.userId === currentUserId}
          users={users}
          onReply={onReply}
          onEdit={onEdit}
          onDelete={onDelete}
          onToggleReaction={onToggleReaction}
        />
      ))}

      {/* Scroll anchor */}
      <div ref={bottomRef} />
    </Box>
  );
};

export default MessageList;
